import { useCallback, useRef } from 'react';
import {
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GridReadyEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { useLocalStorage } from 'react-use';

interface GridState {
  columnState: ColumnState[] | null;
  filters: { [key: string]: any } | null;
}

interface UseGridStatePersistenceProps {
  tableName: string;
  enabled?: boolean;
}

const LS_PREFIX = 'switchGridState_';

export const useGridStatePersistence = ({ tableName, enabled = true }: UseGridStatePersistenceProps) => {
  const stateInitializedRef = useRef(false);
  const [savedState, setSavedState] = useLocalStorage<GridState>(`${LS_PREFIX}${tableName}`, { columnState: null, filters: null });

  const onGridReady = useCallback((event: GridReadyEvent) => {
    if (!enabled || stateInitializedRef.current || !savedState) return;
    const { columnState, filters } = savedState;

    if (columnState) {
      event.api.applyColumnState({ state: columnState, applyOrder: true });
    }
    if (filters) {
      event.api.setFilterModel(filters);
    }

    stateInitializedRef.current = true;
  }, [enabled, savedState]);

  const saveColumnState = useCallback((api: GridReadyEvent['api']) => {
    if (!enabled) return;
    const columnState = api.getColumnState();
    setSavedState((prev) => ({ ...prev!, columnState }));
  }, [enabled, setSavedState]);

  const onColumnResized = useCallback((event: ColumnResizedEvent) => {
    if (!event.finished || event.source !== 'uiColumnDragged') return;
    saveColumnState(event.api);
  }, [saveColumnState]);

  const onSortChanged = useCallback((event: SortChangedEvent) => {
    saveColumnState(event.api);
  }, [saveColumnState]);

  const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
    if (!event.finished) return;
    saveColumnState(event.api);
  }, [saveColumnState]);

  const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
    saveColumnState(event.api);
  }, [saveColumnState]);

  const onColumnPinned = useCallback((event: ColumnPinnedEvent) => {
    saveColumnState(event.api);
  }, [saveColumnState]);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    if (!enabled) return;
    const filterModel = event.api.getFilterModel();
    setSavedState((prev) => ({ ...prev!, filters: filterModel }));
  }, [enabled, setSavedState]);

  const clearSavedState = useCallback(() => {
    setSavedState({ columnState: null, filters: null });
    stateInitializedRef.current = false;
  }, [setSavedState]);

  return {
    onGridReady,
    onColumnResized,
    onSortChanged,
    onColumnMoved,
    onColumnVisible,
    onColumnPinned,
    onFilterChanged,
    clearSavedState,
  };
};
